/**
 * Platform API Service
 * 
 * Main API facade that composes all domain-specific clients
 * This provides a unified interface to all platform services
 */

import { BaseApiClient } from './api/BaseApiClient';
import { RedeemInfo, TokenRedemptionResult } from '../types/api/token';
import { PreferenceType, PreferenceTypeMap } from '../types/preferences';
import { AuthApiClient } from './api/AuthApiClient';
import { ProfileApiClient } from './api/ProfileApiClient';
import { PreferencesApiClient } from './api/PreferencesApiClient';
import { PaymentsApiClient } from './api/PaymentsApiClient';
import { TransactionsApiClient } from './api/TransactionsApiClient';
import { ContactsApiClient } from './api/ContactsApiClient';
import { TokensApiClient } from './api/TokensApiClient';
import { ConfigApiClient } from './api/ConfigApiClient';
import { CurrencyApiClient } from './api/CurrencyApiClient';
import { AnalyticsApiClient } from './api/AnalyticsApiClient';
import { MerchantApiClient } from './api/MerchantApiClient';
import { ProductsApiClient } from './api/ProductsApiClient';
import { PrepaidCardsApiClient } from './api/PrepaidCardsApiClient';
import { DeveloperApiClient } from './api/DeveloperApiClient';
import { AdminApiClient } from './api/AdminApiClient';
import { ReferralApiClient } from './api/ReferralApiClient';
import { EthereumApiClient } from './api/EthereumApiClient';
import { SupportApiClient } from './api/SupportApiClient';
import { UserApiClient } from './api/UserApiClient';
import { NotificationTemplateApiClient } from './api/NotificationTemplateApiClient';
import { CommunicationApiClient } from './api/CommunicationApiClient';
import { CliApiClient } from './api/CliApiClient';
import { currencyService as standaloneCurrencyService } from './currencyService';

/** Parse POST /currency/branding/upload JSON into a usable public URL string. */
function pickMmMoneyBrandingUrlFromUploadResponse(
  kind: 'icon' | 'background',
  res: unknown
): string | null {
  if (!res || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  const pick = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() ? v.trim() : null;

  const nested =
    o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : null;
  const c = nested ?? o;

  if (kind === 'icon') {
    return pick(c.iconUrl) ?? pick(c.currencyIconUrl) ?? pick(c.url) ?? pick(o.url);
  }
  return pick(c.backgroundUrl) ?? pick(c.backgroundImageUrl) ?? pick(c.url) ?? pick(o.url);
}

// Re-export all types for convenience
export * from '../types/api/common';
export * from '../types/api/user';
export * from '../types/api/payment';
export * from '../types/api/merchant';

export class PlatformApi {
  private baseClient: BaseApiClient;
  
  // Domain-specific clients
  public readonly auth: AuthApiClient;
  public readonly profile: ProfileApiClient;
  public readonly preferences: PreferencesApiClient;
  public readonly payments: PaymentsApiClient;
  public readonly transactions: TransactionsApiClient;
  public readonly contacts: ContactsApiClient;
  public readonly tokens: TokensApiClient;
  public readonly config: ConfigApiClient;
  public readonly currency: CurrencyApiClient;
  public readonly analytics: AnalyticsApiClient;
  public readonly merchant: MerchantApiClient;
  public readonly user: UserApiClient;
  public readonly products: ProductsApiClient;
  public readonly prepaidCards: PrepaidCardsApiClient;
  public readonly developer: DeveloperApiClient;
  public readonly admin: AdminApiClient;
  public readonly referrals: ReferralApiClient;
  public readonly ethereum: EthereumApiClient;
  public readonly support: SupportApiClient;
  public readonly notificationTemplates: NotificationTemplateApiClient;
  public readonly communication: CommunicationApiClient;
  public readonly cli: CliApiClient;

  constructor() {
    this.baseClient = new BaseApiClient();
    
    // Initialize domain-specific clients
    this.auth = new AuthApiClient();
    this.profile = new ProfileApiClient();
    this.preferences = new PreferencesApiClient();
    this.payments = new PaymentsApiClient();
    this.transactions = new TransactionsApiClient();
    this.contacts = new ContactsApiClient();
    this.tokens = new TokensApiClient();
    this.config = new ConfigApiClient();
    this.currency = new CurrencyApiClient();
    this.analytics = new AnalyticsApiClient();
    this.merchant = new MerchantApiClient();
    this.products = new ProductsApiClient();
    this.prepaidCards = new PrepaidCardsApiClient();
    this.developer = new DeveloperApiClient();
    this.admin = new AdminApiClient();
    this.referrals = new ReferralApiClient();
    this.ethereum = new EthereumApiClient();
    this.support = new SupportApiClient();
    this.user = new UserApiClient();
    this.notificationTemplates = new NotificationTemplateApiClient();
    this.communication = new CommunicationApiClient();
    this.cli = new CliApiClient();
  }


  /**
   * Upload file with FormData
   */
  async uploadFile<T = any>(endpoint: string, formData: FormData): Promise<T> {
    return this.baseClient.post<T>(endpoint, formData, {
      headers: {
        // Let browser set Content-Type with boundary for multipart/form-data
      }
    });
  }

  /**
   * Set authentication token across all clients
   */
  setToken(token: string): void {
    this.baseClient.setToken(token);
    this.auth.setToken(token);
    this.profile.setToken(token);
    this.preferences.setToken(token);
    this.payments.setToken(token);
    this.transactions.setToken(token);
    this.contacts.setToken(token);
    this.tokens.setToken(token);
    this.config.setToken(token);
    this.currency.setToken(token);
    this.analytics.setToken(token);
    this.merchant.setToken(token);
    this.products.setToken(token);
    this.prepaidCards.setToken(token);
    this.developer.setToken(token);
    this.admin.setToken(token);
    this.referrals.setToken(token);
    this.ethereum.setToken(token);
    this.support.setToken(token);
    this.user.setToken(token);
    this.notificationTemplates.setToken(token);
    this.cli.setToken(token);
  }

  /**
   * Remove authentication token from all clients
   */
  removeToken(): void {
    this.baseClient.removeToken();
    this.auth.removeToken();
    this.profile.removeToken();
    this.preferences.removeToken();
    this.payments.removeToken();
    this.transactions.removeToken();
    this.contacts.removeToken();
    this.tokens.removeToken();
    this.config.removeToken();
    this.currency.removeToken();
    this.analytics.removeToken();
    this.merchant.removeToken();
    this.products.removeToken();
    this.prepaidCards.removeToken();
    this.developer.removeToken();
    this.admin.removeToken();
    this.referrals.removeToken();
    this.ethereum.removeToken();
    this.support.removeToken();
    this.user.removeToken();
    this.notificationTemplates.removeToken();
    this.cli.removeToken();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.baseClient.isAuthenticated();
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return this.baseClient.getToken();
  }

  // ============================================
  // Generic HTTP methods
  // ============================================
  
  /**
   * Make a direct API request using the base client
   */
  async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.baseClient.request<T>(endpoint, options);
  }

  /**
   * Make a GET request using the base client
   */
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.baseClient.get<T>(endpoint, options);
  }

  /**
   * Make a POST request using the base client
   */
  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.baseClient.post<T>(endpoint, data, options);
  }

  /**
   * Make a PUT request using the base client
   */
  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.baseClient.put<T>(endpoint, data, options);
  }

  /**
   * Make a PATCH request using the base client
   */
  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.baseClient.patch<T>(endpoint, data, options);
  }

  /**
   * Make a DELETE request using the base client
   */
  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.baseClient.delete<T>(endpoint, options);
  }

  // ============================================
  // Convenience methods that delegate to domain clients
  // ============================================

  // Auth methods
  async login(usernameOrEmail: string, password: string) {
    const result = await this.auth.login(usernameOrEmail, password);
    this.setToken(result.token);
    return result;
  }

  async guestLogin() {
    const result = await this.auth.guestLogin();
    this.setToken(result.token);
    return result;
  }

  async requestPasswordReset(email: string) {
    return this.auth.requestPasswordReset(email);
  }

  async register(userData: any) {
    return this.auth.register(userData);
  }

  /**
   * Request OTP code for login
   */
  async requestOtp(identifier: string, type: 'email' | 'sms') {
    return this.auth.requestOtp(identifier, type);
  }

  async getOtpStatus(type: 'email' | 'sms', identifier: string) {
    return this.auth.getOtpStatus(type, identifier);
  }

  /**
   * Verify OTP code and login
   */
  async verifyOtp(identifier: string, code: string, type: 'email' | 'sms') {
    const result = await this.auth.verifyOtp(identifier, code, type);
    if (result.token) {
      this.setToken(result.token);
    }
    return result;
  }

  async logout() {
    const result = await this.auth.logout();
    this.removeToken();
    return result;
  }

  // Profile methods
  async getProfile(fields?: string | string[]) {
    return this.profile.getProfile(fields);
  }

  async patchProfile(partial: Record<string, unknown>) {
    return this.profile.patchProfile(partial);
  }

  /** GET `/api/v1/currency` — issuer MeMoney row (defaults merged by platform). */
  async getIssuerMeMoneyCurrency(): Promise<unknown> {
    return this.currency.getCurrencyMeMoneyIssuer();
  }

  async getBalance(currency: string) {
    return this.profile.getBalance(currency);
  }

  async getBalances() {
    return this.profile.getBalances();
  }

  async getBankProfile() {
    return this.profile.getBankProfile();
  }

  async updateBankProfile(bankProfile: any) {
    return this.profile.updateBankProfile(bankProfile);
  }

  async getBankByBIC(bic: string) {
    return this.profile.getBankByBIC(bic);
  }

  async getAllBanks() {
    return this.profile.getAllBanks();
  }

  // Payment methods
  async getPaymentMethods() {
    return this.payments.getPaymentMethods();
  }

  async setDefaultPaymentMethod(methodId: string) {
    return this.payments.setDefaultPaymentMethod(methodId);
  }

  async removePaymentMethod(methodId: string) {
    return this.payments.removePaymentMethod(methodId);
  }

  async createStripeSetupIntent() {
    return this.payments.createStripeSetupIntent();
  }

  async confirmStripePaymentMethod(paymentMethodId: string, cardData: any) {
    return this.payments.confirmStripePaymentMethod(paymentMethodId, cardData);
  }

  async chargeAndSaveStripeCard(chargeData: {
    amount: number;
    currency: string;
    paymentMethodId: string;
  }) {
    return this.payments.chargeAndSaveStripeCard(chargeData);
  }

  async createPlaidLinkToken() {
    return this.payments.createPlaidLinkToken();
  }

  async exchangePlaidPublicToken(publicToken: string, accountData: any) {
    return this.payments.exchangePlaidPublicToken(publicToken, accountData);
  }

  // Stripe Financial Connections (Bank Account) methods
  async getStripeBankAccounts() {
    return this.payments.getStripeBankAccounts();
  }

  async createStripeBankSession() {
    return this.payments.createStripeBankSession();
  }

  async attachStripeBankAccount(financialConnectionsAccountId: string, billingName: string) {
    return this.payments.attachStripeBankAccount(financialConnectionsAccountId, billingName);
  }

  async saveStripeBankAccount(setupIntentId: string) {
    return this.payments.saveStripeBankAccount(setupIntentId);
  }

  async connectPayPalAccount(paypalData: any) {
    return this.payments.connectPayPalAccount(paypalData);
  }

  async topUpWithStripe(topUpData: any) {
    return this.payments.topUpWithStripe(topUpData);
  }

  async topUpWithACH(topUpData: any) {
    return this.payments.topUpWithACH(topUpData);
  }

  async topUpWithPayPal(topUpData: any) {
    return this.payments.topUpWithPayPal(topUpData);
  }

  async paypalPayout(payoutData: any) {
    return this.payments.paypalPayout(payoutData);
  }

  async stripeConnectPayout(payoutData: {
    amount: number;
    currency: string;
    stripeConnectId: string;
  }) {
    return this.payments.stripeConnectPayout(payoutData);
  }

  async transferOut(transferData: any) {
    return this.payments.transferOut(transferData);
  }

  async getTransferOutPreferences() {
    return this.payments.getTransferOutPreferences();
  }

  async sendPayPalPayoutEmailVerification(email: string) {
    return this.payments.sendPayPalPayoutEmailVerification(email);
  }

  async verifyPayPalPayoutEmail(code: string) {
    return this.payments.verifyPayPalPayoutEmail(code);
  }

  async addBalance(data: any) {
    return this.payments.addBalance(data);
  }

  async processGuestPayment(paymentData: any) {
    return this.payments.processGuestPayment(paymentData);
  }

  async notifyMerchantOfGuestPayment(notificationData: any) {
    return this.payments.notifyMerchantOfGuestPayment(notificationData);
  }

  // Transaction methods
  async getTransactions(filters?: any) {
    return this.transactions.getTransactions(filters);
  }

  async getTransactionsBatch(transactionIds: string[], userId?: string) {
    return this.transactions.getTransactionsBatch(transactionIds, userId);
  }

  async exportTransactions(format: 'csv' | 'pdf', filters?: any) {
    return this.transactions.exportTransactions(format, filters);
  }

  async getTokenDetails(tokenKey: string) {
    return this.transactions.getTokenDetails(tokenKey);
  }

  // Token methods
  async createToken(tokenData: any) {
    return this.tokens.createToken(tokenData);
  }

  async revealToken(tokenKey: string, transactor: string, redeemAmount?: number) {
    return this.tokens.revealToken(tokenKey, transactor, redeemAmount);
  }

  async payInvoiceToken(tokenKey: string, redeemInfo: RedeemInfo) {
    return this.tokens.payInvoiceToken(tokenKey, redeemInfo);
  }

  /**
   * Redeem a token
   * @param transactor - 'balance' for cash/invoice (default), 'identity' for ID tokens
   */
  async redeemToken(
    tokenKey: string,
    redeemAmount?: number,
    transactor: 'balance' | 'identity' = 'balance'
  ): Promise<TokenRedemptionResult> {
    const payload: Record<string, unknown> = {
      key: tokenKey,
      transactor,
      redeem: true,
    };
    if (transactor === 'balance' && redeemAmount !== undefined) {
      payload.amount = redeemAmount;
    }
    return this.tokens.post('/trigger', payload);
  }

  async getInvoiceDetails(invoiceCode: string) {
    return this.tokens.getInvoiceDetails(invoiceCode);
  }

  async payInvoice(paymentData: any) {
    return this.tokens.payInvoice(paymentData);
  }

  // Merchant methods
  async getMerchantProfile(merchantId?: string) {
    return this.merchant.getMerchantProfile(merchantId);
  }

  async getPublicMerchantProfilesBatch(merchantIds: string[]) {
    return this.merchant.getPublicMerchantProfilesBatch(merchantIds);
  }

  async updateMerchantProfile(formData: any) {
    return this.merchant.updateMerchantProfile(formData);
  }

  async patchMerchantProfile(updates: any) {
    return this.merchant.patchMerchantProfile(updates);
  }

  async addMerchantAddress(addressData: any) {
    return this.merchant.addMerchantAddress(addressData);
  }

  async deleteMerchantAddress(addressId: string) {
    return this.merchant.deleteMerchantAddress(addressId);
  }

  async createStripeConnectAccount() {
    return this.merchant.initiateMerchantStripeConnect();
  }

  async disconnectMerchantStripeConnect() {
    return this.merchant.disconnectMerchantStripeConnect();
  }

  async getMerchantFiles() {
    return this.merchant.getMerchantFiles();
  }

  async uploadMerchantFile(file: File) {
    return this.merchant.uploadMerchantFile(file);
  }

  /**
   * MM$ issuer imagery — POST `/api/v1/currency/branding/upload` (do not use merchant file upload).
   * @param kind `icon` (favicon-style) or `background` card backdrop.
   */
  async uploadMmMoneyImageFile(file: File, kind: 'icon' | 'background'): Promise<string> {
    if (!file.type.startsWith('image/')) {
      throw new Error('Only images are supported');
    }
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('Image exceeds upload size limit.');
    }

    const data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Could not read image'));
      reader.readAsDataURL(file);
    });

    const res = await this.currency.uploadCurrencyBranding({
      type: kind,
      filename: file.name,
      mimetype: file.type,
      size: file.size,
      data,
    });

    const url = pickMmMoneyBrandingUrlFromUploadResponse(kind, res);
    if (!url) {
      throw new Error('Upload did not return an image URL');
    }
    return url;
  }

  async deleteMerchantFile(fileId: string) {
    return this.merchant.deleteMerchantFile(fileId);
  }

  async getMerchantFollowers() {
    return this.merchant.getMerchantFollowers();
  }

  async removeMerchantFollower(followerId: string) {
    return this.merchant.removeMerchantFollower(followerId);
  }

  async getMerchantAnnouncements() {
    return this.merchant.getMerchantAnnouncements();
  }

  async sendMerchantAnnouncement(data: any) {
    return this.merchant.sendMerchantAnnouncement(data);
  }

  async generateMerchantQRCode(qrData: any) {
    return this.merchant.generateMerchantQRCode(qrData);
  }

  async updateMerchantSettings(settingsData: any) {
    return this.merchant.updateMerchantSettings(settingsData);
  }

  async requestMerchant(merchantData: any) {
    return this.merchant.requestMerchant(merchantData);
  }

  async getUserFollowedMerchants() {
    return this.merchant.getUserFollowedMerchants();
  }

  async unfollowMerchant(merchantId: string) {
    return this.merchant.unfollowMerchant(merchantId);
  }

  // Communication methods
  async subscribeToMerchant(merchantId: string) {
    return this.communication.subscribeToMerchant(merchantId);
  }

  async unsubscribeFromMerchant(merchantId: string) {
    return this.communication.unsubscribeFromMerchant(merchantId);
  }

  async getSubscriptions() {
    return this.communication.getSubscriptions();
  }

  async blockMerchant(merchantId: string, reason?: string) {
    return this.communication.blockMerchant(merchantId, reason);
  }

  async getBlockedMerchants() {
    return this.communication.getBlockedMerchants();
  }

  async unblockMerchant(merchantId: string) {
    return this.communication.unblockMerchant(merchantId);
  }

  async getInboxMessages(filters?: {
    unreadOnly?: boolean;
    merchantId?: string;
    type?: 'announcement' | 'conversation' | 'all';
    limit?: number;
    offset?: number;
  }) {
    return this.communication.getInboxMessages(filters);
  }

  async markMessageAsRead(messageId: string) {
    return this.communication.markMessageAsRead(messageId);
  }

  async markAllMessagesAsRead(merchantId?: string) {
    return this.communication.markAllMessagesAsRead(merchantId);
  }

  async getCommunicationPreferences() {
    return this.communication.getCommunicationPreferences();
  }

  async updateCommunicationPreferences(preferences: {
    autoSubscribe: boolean;
    emailNotifications?: boolean;
    pushNotifications?: boolean;
  }) {
    return this.communication.updateCommunicationPreferences(preferences);
  }

  // Product methods
  async getProducts(filters?: any) {
    return this.products.getProducts(filters);
  }

  async getProductCategories() {
    return this.products.getProductCategories();
  }

  async createProduct(productData: any) {
    return this.products.createProduct(productData);
  }

  async updateProduct(productId: string, productData: any) {
    return this.products.updateProduct(productId, productData);
  }

  async deleteProduct(productId: string) {
    return this.products.deleteProduct(productId);
  }

  // Contact methods
  async getContacts(options?: {
    role?: 'MERCHANT' | 'USER';
    includeTransactions?: boolean;
  }) {
    return this.contacts.getContacts(options);
  }

  async getContact(userId: string, options?: {
    includeTransactions?: boolean;
  }) {
    return this.contacts.getContact(userId, options);
  }

  async addContact(contactData: any) {
    return this.contacts.addContact(contactData);
  }

  async getConversations(filters?: any) {
    return this.contacts.getConversations(filters);
  }

  async getConversation(transactionId: string) {
    return this.contacts.getConversation(transactionId);
  }

  async sendMessage(transactionId: string, message: string) {
    return this.contacts.sendMessage(transactionId, message);
  }

  /**
   * Get conversation using API v2
   */
  async getConversationV2(transactionId: string, includeWall = false) {
    return this.contacts.getConversationV2(transactionId, includeWall);
  }

  /**
   * Send message using API v2
   */
  async sendMessageV2(transactionId: string, message: string) {
    console.log('🔵 [platformApi] sendMessageV2 called:', { transactionId, messageLength: message.length });
    try {
      const result = await this.contacts.sendMessageV2(transactionId, message);
      console.log('✅ [platformApi] sendMessageV2 success:', result);
      return result;
    } catch (error: any) {
      console.error('❌ [platformApi] sendMessageV2 error:', error);
      throw error;
    }
  }

  async uploadAttachment(file: File, transactionId: string) {
    return this.contacts.uploadAttachment(file, transactionId);
  }

  /**
   * Get direct conversation with contact (v2 API)
   * @param contactId - The contact's user ID (contact.id)
   */
  async getDirectConversation(contactId: string) {
    return this.contacts.getDirectConversationV2(contactId);
  }

  /**
   * Send direct message to contact (v2 API)
   * @param contactId - The contact's user ID (contact.id)
   * @param message - Message text
   */
  async sendDirectMessage(contactId: string, message: string) {
    console.log('🔵 [platformApi] sendDirectMessage called:', { 
      contactId, 
      messageLength: message.length 
    });
    try {
      const result = await this.contacts.sendDirectMessageV2(contactId, message);
      console.log('✅ [platformApi] sendDirectMessage success:', result);
      return result;
    } catch (error: any) {
      console.error('❌ [platformApi] sendDirectMessage error:', error);
      throw error;
    }
  }

  // V2 Inbox API methods
  /**
   * Get contacts summary for inbox view (v2 API)
   * This is the recommended endpoint for building a messaging inbox view.
   * Reference: API-REFERENCE-MANUAL.md line 3524
   * @param includeRead Include contacts with no unread items (default: false)
   */
  async getContactsSummary(includeRead: boolean = false) {
    return this.contacts.getContactsSummary(includeRead);
  }

  /**
   * Get direct messages with a specific contact (v2 API)
   * Reference: API-REFERENCE-MANUAL.md line 3610
   * @param contactId The contact/user ID
   * @param options Pagination and filtering options
   */
  async getContactMessages(
    contactId: string,
    options?: {
      limit?: number;
      offset?: number;
      since?: string;
    }
  ) {
    return this.contacts.getContactMessages(contactId, options);
  }

  /**
   * Get posts from a specific contact/merchant (v2 API)
   * Reference: API-REFERENCE-MANUAL.md line 3580
   * @param contactId The contact/user ID (merchant)
   * @param options Pagination and filtering options
   */
  async getContactPosts(
    contactId: string,
    options?: {
      limit?: number;
      offset?: number;
      since?: string;
    }
  ) {
    return this.contacts.getContactPosts(contactId, options);
  }

  /**
   * Mark a direct message as read (v2 API)
   * Reference: API-REFERENCE-MANUAL.md line 3641
   * @param contactId The contact/user ID
   * @param messageId The message ID to mark as read
   */
  async markContactMessageAsRead(contactId: string, messageId: string) {
    return this.contacts.markContactMessageAsRead(contactId, messageId);
  }

  // Preferences methods
  async getPreferences<T extends keyof PreferenceTypeMap>(
    type: T
  ): Promise<PreferenceType<T>> {
    return this.preferences.getPreferences(type);
  }

  async updatePreferences<T extends keyof PreferenceTypeMap>(
    type: T,
    prefs: PreferenceType<T>
  ): Promise<void> {
    return this.preferences.updatePreferences(type, prefs);
  }

  // Use getPreferences<T>('auto-deposit') instead of these specific methods

  // Config methods
  async getStripeConfig() {
    return this.config.getStripeConfig();
  }

  async getPlaidConfig() {
    return this.config.getPlaidConfig();
  }

  async getAppConfig(appName?: string) {
    return this.config.getAppConfig(appName);
  }

  async getAppConfiguration(appId: string) {
    return this.developer.getAppConfiguration(appId);
  }

  async updateAppConfiguration(appId: string, config: any) {
    return this.developer.updateAppConfiguration(appId, config);
  }

  // Currency methods
  async getCurrencyDetails(code: string) {
    const client = this.currency;
    if (!client) {
      return standaloneCurrencyService.getCurrencyDetails(code);
    }
    return client.getCurrencyDetails(code);
  }

  async formatCurrency(amount: number, currencyCode: string) {
    const client = this.currency;
    if (!client) {
      return standaloneCurrencyService.formatCurrency(amount, currencyCode);
    }
    return client.formatCurrency(amount, currencyCode);
  }

  async preloadCurrencies(currencyCodes: string[]) {
    const client = this.currency;
    if (!client) {
      return standaloneCurrencyService.preloadCurrencies(currencyCodes);
    }
    return client.preloadCurrencies(currencyCodes);
  }

  clearCurrencyCache() {
    const client = this.currency;
    if (!client) {
      return standaloneCurrencyService.clearCache();
    }
    return client.clearCurrencyCache();
  }

  getCurrencyCacheStats() {
    const client = this.currency;
    if (!client) {
      return standaloneCurrencyService.getCacheStats();
    }
    return client.getCurrencyCacheStats();
  }

  async getAcceptableCurrencies() {
    const client = this.currency;
    if (!client) {
      return new CurrencyApiClient().getAcceptableCurrencies();
    }
    return client.getAcceptableCurrencies();
  }

  async getCentralBankFiatCurrencies() {
    const client = this.currency;
    if (!client) {
      return new CurrencyApiClient().getCentralBankFiatCurrencies();
    }
    return client.getCentralBankFiatCurrencies();
  }

  // Analytics methods
  async getStats(options?: any) {
    return this.analytics.getStats(options);
  }

  // Developer methods
  async getUserDeveloperApps() {
    return this.developer.getUserDeveloperApps();
  }

  async createDeveloperApp(appData: any) {
    return this.developer.createDeveloperApp(appData);
  }

  async joinDeveloperApp(invitationCode: string) {
    return this.developer.joinDeveloperApp(invitationCode);
  }

  async regenerateApiKey(appId: string) {
    return this.developer.regenerateApiKey(appId);
  }

  async regenerateInvitationCode(appId: string) {
    return this.developer.regenerateInvitationCode(appId);
  }

  // Prepaid card methods (legacy compatibility)
  async issueBoPaymentsCard(amount: number, currency: string) {
    return this.prepaidCards.issueCard(amount, currency);
  }

  async convertTokenToBoPaymentsCard(tokenKey: string) {
    return this.prepaidCards.convertTokenToPrepaidCard(tokenKey);
  }

  async getPrepaidCardForToken(tokenKey: string) {
    return this.prepaidCards.getPrepaidCardForToken(tokenKey);
  }

  async getBoPaymentsCardBalance(uniqueUserId: string) {
    return this.prepaidCards.getCardBalance(uniqueUserId);
  }

  async getBoPaymentsCardTransactions(uniqueUserId: string) {
    return this.prepaidCards.getCardTransactions(uniqueUserId);
  }

  async closeBoPaymentsCard(uniqueUserId: string, tokenId: string) {
    return this.prepaidCards.closeCard(uniqueUserId, tokenId);
  }

  async loadBoPaymentsCard(amount: number, description: string, tokenId: string) {
    return this.prepaidCards.loadCard(amount, description, tokenId);
  }

  async getBoPaymentsConfig() {
    return this.prepaidCards.getConfig();
  }

  // ============================================
  // Admin methods
  // ============================================

  // Admin Dashboard and Analytics
  async getAdminDashboardStats(options?: any) {
    return this.admin.getDashboardStats(options);
  }

  async getAdminSignupStats(dateRange?: string) {
    return this.admin.getSignupStats(dateRange);
  }

  async getAdminSignupAttribution(dateRange?: string) {
    return this.admin.getSignupAttribution(dateRange);
  }

  async getAdminSignupTrends(dateRange?: string) {
    return this.admin.getSignupTrends(dateRange);
  }

  async getAdminSignupDevices(dateRange?: string) {
    return this.admin.getSignupDevices(dateRange);
  }

  async getAdminSignupLocations(dateRange?: string) {
    return this.admin.getSignupLocations(dateRange);
  }

  // Admin User Management
  async getAdminUsers(filters?: any) {
    return this.admin.getUsers(filters);
  }

  async getAdminUser(userId: string) {
    return this.admin.getUser(userId);
  }

  async createAdminUser(userData: any) {
    return this.admin.createUser(userData);
  }

  async updateAdminUser(userId: string, updates: any) {
    return this.admin.updateUser(userId, updates);
  }

  async deleteAdminUser(userId: string) {
    return this.admin.deleteUser(userId);
  }

  async updateAdminUserRoles(userId: string, roles: string[]) {
    return this.admin.updateUserRoles(userId, roles);
  }

  async getAdminUserBalance(userId: string) {
    return this.admin.getUserBalance(userId);
  }

  async updateAdminUserBalance(userId: string, currency: string, amount: number, operation: 'add' | 'subtract' | 'set') {
    return this.admin.updateUserBalance(userId, currency, amount, operation);
  }

  // Admin Transaction Management
  async getAdminTransactions(filters?: any) {
    return this.admin.getTransactions(filters);
  }

  async getAdminTransaction(transactionId: string) {
    return this.admin.getTransaction(transactionId);
  }

  async updateAdminTransaction(transactionId: string, updates: any) {
    return this.admin.updateTransaction(transactionId, updates);
  }

  async deleteAdminTransaction(transactionId: string) {
    return this.admin.deleteTransaction(transactionId);
  }

  // Admin Guest Conversations
  async getAdminGuestConversations(filters?: any) {
    return this.admin.getGuestConversations(filters);
  }

  async logAdminGuestConversation(data: any) {
    return this.admin.logGuestConversation(data);
  }

  // Admin Referral Management
  async getAdminReferralConfig() {
    return this.admin.getReferralConfig();
  }

  async updateAdminReferralConfig(config: any) {
    return this.admin.updateReferralConfig(config);
  }

  async getAdminReferralStats() {
    return this.admin.getReferralStats();
  }

  async getAdminRecentReferrals(limit?: number) {
    return this.admin.getRecentReferrals(limit);
  }

  async getAdminReferralCodes(filters?: any) {
    return this.admin.getReferralCodes(filters);
  }

  async createAdminReferralCode(referralData: any) {
    return this.admin.createReferralCode(referralData);
  }

  // Admin System Management
  async getAdminSystemSettings() {
    return this.admin.getSystemSettings();
  }

  async updateAdminSystemSettings(settings: any) {
    return this.admin.updateSystemSettings(settings);
  }

  async getAdminSystemHealth() {
    return this.admin.getSystemHealth();
  }

  async getAdminAuditLogs(filters?: any) {
    return this.admin.getAuditLogs(filters);
  }

  // Admin Export Functions
  async exportAdminUsers(format: 'csv' | 'json', filters?: any) {
    return this.admin.exportUsers(format, filters);
  }

  async exportAdminTransactions(format: 'csv' | 'json', filters?: any) {
    return this.admin.exportTransactions(format, filters);
  }

  // ============================================
  // Referral methods
  // ============================================

  // Referral Dashboard
  async getReferralDashboard() {
    return this.referrals.getReferralDashboard();
  }

  // Referral Tokens
  async getReferralTokens(params?: any) {
    return this.referrals.getReferralTokens(params);
  }

  async createReferralToken(tokenData: any) {
    return this.referrals.createReferralToken(tokenData);
  }

  async getReferralTokenDetails(tokenId: string) {
    return this.referrals.getReferralTokenDetails(tokenId);
  }

  async cancelReferralToken(tokenId: string) {
    return this.referrals.cancelReferralToken(tokenId);
  }

  // Referral Rewards
  async getReferralRewards(params?: any) {
    return this.referrals.getReferralRewards(params);
  }

  // Referral Milestones
  async getReferralMilestones() {
    return this.referrals.getReferralMilestones();
  }

  async claimMilestoneReward(milestoneId: string, rewardChoice: any) {
    return this.referrals.claimMilestoneReward(milestoneId, rewardChoice);
  }

  // Referral Stats and Limits
  async checkReferralTokenLimit() {
    return this.referrals.checkReferralTokenLimit();
  }

  async getReferralStats(period?: any) {
    return this.referrals.getReferralStats(period);
  }

  // ============================================
  // CLI and Script methods (developer features)
  // ============================================

  async createCLISession() {
    return this.developer.createCLISession();
  }

  async uploadScript(scriptData: any) {
    return this.developer.uploadScript(scriptData);
  }

  async getCLIHistory(limit?: number) {
    return this.developer.getCLIHistory(limit);
  }

  async executeCLICommand(command: string) {
    return this.developer.executeCLICommand(command);
  }

  // Utility methods
  getWebSocketUrl(): string {
    const baseUrl = this.baseClient.defaults.baseURL || '';
    return baseUrl.replace(/^http/, 'ws');
  }
}

// Create and export singleton instance FIRST
export const platformApi = new PlatformApi();

// Create prepaid card API service (legacy compatibility) - AFTER platformApi is created
export const boPaymentsApi = {
  async issueCard(amount: number, currency: string) {
    return platformApi.prepaidCards.issueCard(amount, currency);
  },
  async convertTokenToPrepaidCard(tokenKey: string) {
    return platformApi.prepaidCards.convertTokenToPrepaidCard(tokenKey);
  },
  async getPrepaidCardForToken(tokenKey: string) {
    return platformApi.prepaidCards.getPrepaidCardForToken(tokenKey);
  },
  async getCardBalance(uniqueUserId: string) {
    return platformApi.prepaidCards.getCardBalance(uniqueUserId);
  },
  async getCardTransactions(uniqueUserId: string) {
    return platformApi.prepaidCards.getCardTransactions(uniqueUserId);
  },
  async closeCard(uniqueUserId: string, tokenId: string) {
    return platformApi.prepaidCards.closeCard(uniqueUserId, tokenId);
  },
  async loadCard(amount: number, description: string, tokenId: string) {
    return platformApi.prepaidCards.loadCard(amount, description, tokenId);
  },
  async getConfig() {
    return platformApi.prepaidCards.getConfig();
  }
};

// Export default for easy importing
export default platformApi;

// Re-export currency types for convenience
export type { CurrencyDetails } from './currencyService'; 
